<template>
    <div>
        <h1>Perkins Indicators</h1>
        <!-- <hr/> -->
        <fieldset :disabled="formDisabled">
            <b-row>
                <b-col>
                    <b-table
                        bordered
                        striped
                        hover
                        responsive
                        outlined
                        small
                        class="info-text"
                        :fields="indicatorsFields"
                        :items="perkinsIndicatorItems"
                        show-empty
                        empty-text="There are currently no records to show."
                    >
                        <template v-if="needsToShowPrevious" v-slot:cell(previous)="row">
                            <b-form-checkbox
                                :key="row.index + 99"
                                v-model="previousCheckedItems"
                                name="previous-checked"
                                size="lg"
                                disabled
                                :value="row.item.id"
                            />
                        </template>
                        <template v-slot:cell(action)="row">
                            <b-form-checkbox
                                :key="row.index"
                                v-model="checkedItems"
                                size="lg"
                                name="checked"
                                :value="row.item.id"
                                :disabled="wrongProposalType(row)"
                                @click.native.stop
                            />
                        </template>

                        <template v-slot:cell(indicatorDisplayName)="data">
                            {{ data.item.indicatorDisplayName }}
                        </template>

                        <template v-slot:cell(priorPerformance)="data">
                            {{ (data.item.priorPerformance + '%') || 'Loading...' }}
                        </template>

                        <template v-slot:cell(target)="data">
                            {{ (data.item['targetAy' + targetAy] + '%') || 'Loading...' }}
                        </template>

                        <template v-slot:cell(improvementPlanYear)="data">
                            {{ data.item.improvementPlanYear }}
                        </template>

                        <template v-slot:cell()="data">
                            {{
                                formatIndicatorTarget(data.item.perkinsIndicator, data.field.key)
                            }}
                        </template>
                    </b-table>
                </b-col>
            </b-row>
        </fieldset>
        <hr>
        <b-row>
            <b-col>
                <module-actions
                    :is-loading="isSaving"
                    background-color="#006438"
                    @saveForm="saveForm"
                />
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';

export default {
    name: 'PerkinsIndicators',

    props: {
        formDisabled: { type: Boolean, default: true }
    },

    data() {
        return {
            form: {},
            checkedItems: [],
            previousCheckedItems: [],
            isSaving: false
        };
    },

    computed: {
        ...mapState('ref/perkinsIndicators', {
            perkinsIndicatorPerformance: 'perkinsPerformanceWithIndicatorDetails',
        }),

        ...mapState('localApplication/module/baselineAndTargets', {
            indicatorTargets(state) { return state.data.content.indicatorTargets; },
        }),

        ...mapGetters({
            proposalData: 'proposal/proposalData',
            moduleData: 'module/moduleData',
            moduleDataPrevious: 'module/moduleDataPrevious',
            needsToShowPrevious: 'review/needsToShowPrevious',
            previousProposalPerkinsIndicator: 'review/previousProposalPerkinsIndicator',
            isAuthorized: 'auth/isAuthorized',
            isReviewer: 'auth/isReviewer',
            currentAy: 'ref/global/currentAy',
            applicantId: 'application/applicantId',
            applicationAy: 'application/applicationAy',
        }),

        targetAy() {
            return this.applicationAy;
        },

        payload() {
            return {
                content: [...this.checkedItems]
            };
        },

        indicatorsFields() {
            let fields = [
                { key: 'action', label: 'Proposal Focus' },
                { key: 'indicatorDisplayName', label: 'Core Indicator' },
                { key: 'priorPerformance', label: 'Prior Performance' },
                // Prior Achievement
                // { key: 'ay20', label: '2019-20 Actual' },
                // Current Target
                { key: 'target', label: 'Upcoming AY Target' },
                { key: 'improvementPlanYear', label: 'Improvement Plan Status' },
            ];

            if (this.previousProposalPerkinsIndicator) {
                fields.unshift({ key: 'previous' });
            }

            return fields;
        },

        applicationPerkinsPerformance() {
            if (this.isReviewer) {
                return this.perkinsIndicatorPerformance.filter((indicator) => {
                    return indicator.applicantId === this.applicantId;
                });
            }

            return this.perkinsIndicatorPerformance;
        },

        perkinsIndicatorItems() {
            if (this.applicationPerkinsPerformance) {
                return this.applicationPerkinsPerformance.map((indicator) => {
                    let rowVariant = indicator.improvementPlanYear > 0 ? 'danger' : null;

                    return {
                        ...indicator,
                        _rowVariant: rowVariant,
                    };
                });
            }

            return [];
        },
    },

    created() {
        if (this.moduleData) {
            this.checkedItems = this.moduleData.content || this.checkedItems;
        }

        if (this.previousProposalPerkinsIndicator) {
            this.previousCheckedItems = this.previousProposalPerkinsIndicator || this.previousCheckedItems;
        }
    },

    methods: {
        // TODO/Refactor: Add getters to proposal store to determine deficient proposal types
        // TODO/Refactor: Renaming some of these might make this clearer
        wrongProposalType(item) {
            /* mjeffe 2023-07-06:
             * I don't understand this logic, it will take a lot more digging.
             * I think it intended to disable the 3P1 deficient indicator on
             * non-deficient proposal types. But once I implemented deficient
             * proposal types, the 3P1 was disabled on them as well, so I'm not
             * sure if it's buggy, or it intended to do something else.
             *
             * For now, I've simply disabled it to allow them to be selected
             * and let reviewers sort it out * if some numbskull selects one on
             * a non-deficient proposal.
             */
            return false;
            /*
            if (item.item._rowVariant || item.item._rowVariant !== undefined) {
                return [20, 21].includes(this.proposalData.ref_proposal_type_id)
                    ? ['null', null].includes(item.item._rowVariant)
                    : !['null', null].includes(item.item._rowVariant);
            }
            */
        },

        saveForm() {
            this.isSaving = true;
            this.$store.dispatch('module/saveModule', { ...this.payload })
                .then((response) => {
                    this.isSaving = false;
                    Log.debug(response, `COMPONENT:${this.name} -> Save module response:`);
                })
                .catch((arcAlert) => {
                    this.isSaving = false;
                });
        },

        formatIndicatorTarget(perkinsIndicator, targetKey) {
            return this.indicatorTargets[perkinsIndicator] &&
                this.indicatorTargets[perkinsIndicator][targetKey]
                ? this.indicatorTargets[perkinsIndicator][targetKey] + '%'
                : ' No Target Data ';
        },
    }
};
</script>

<style scoped lang="scss">
@import "$StyleVars";

h1 {
    font-size: 1.5rem;
    text-align: left;
    font-weight: bold;
    color: $ace-purple;
}
</style>
